import { Alert, AlertTitle, Box, Grid, TextField, Typography } from '@mui/material';
import { TranslateFunction } from '../../../../hooks/useTranslate';
import { DrawerHeader } from '../../../../components/Drawer/DrawerHeader';

import { Visita } from '../../consts/visitasTypes';
import ButtonActions from '../../../../components/ButtonActions';
import BackdropComponent from '../../../../components/Backdrop/BackdropComponent';
import useVisitActions from '../../hooks/useVisitActions';
import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useGetVisitaByIdQuery } from '../../../../services/visitasApi';
import { formatFullName } from '../../../../utils/common';

interface SendVisitToRevisionDrawerProps {
    translate: TranslateFunction;
    selected: Visita | null;
    setOpen: (value: boolean) => void;
    onSuccess?: () => void;
    actionType: string;
}

// Consultora con el criterio de la grilla de Visitas: la del preventor y, si no
// tiene, la de la visita (SMP-580).
const getConsultora = (visita: any): string | undefined =>
    visita?.preventor?.proveedor?.razonSocial || visita?.proveedor?.razonSocial;

const SendVisitToRevisionDrawer = ({
    translate,
    selected,
    setOpen,
    onSuccess,
    actionType
}: SendVisitToRevisionDrawerProps) => {
    const { handleSendVisitToRevisionClick, isSendToRevisionLoading } = useVisitActions();
    const [motive, setMotive] = useState<string>('');

    // Las visitas de la pestaña del siniestro (ROAM) no traen la consultora:
    // se completa con el detalle de la visita (SMP-583).
    const consultoraSeleccionada = getConsultora(selected);
    const { data: visitaDetalle } = useGetVisitaByIdQuery(
        { idVisita: Number(selected?.id) },
        { skip: !selected?.id || Boolean(consultoraSeleccionada) }
    );
    const consultora = consultoraSeleccionada || getConsultora(visitaDetalle?.data) || '-';
    const preventor = (selected as any)?.preventor;
    const nombrePreventor = formatFullName(preventor?.nombre, preventor?.apellido) || '-';

    const closeDrawer = () => {
        setOpen(false);
    };

    const handleSubmit = () => {
        if (motive.trim() === '') {
            enqueueSnackbar(translate('drawers.sendVisitToRevision.alerts.motiveRequired'), {
                variant: 'error'
            });
            return;
        }
        handleSendVisitToRevisionClick(selected?.id || 0, motive, () => {
            onSuccess?.();
            closeDrawer();
        });
    };

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            padding={1}
            sx={{ flexDirection: 'column', height: '100%' }}
        >
            <Grid>
                <DrawerHeader
                    onIconClick={() => setOpen(false)}
                    text={translate(`drawer.title.${actionType?.toLowerCase()}`)}
                />
                <Alert severity="info">
                    <AlertTitle>
                        <Typography fontSize={'1rem'} fontWeight={'700'}>
                            {translate('drawers.sendVisitToRevision.info.title')}
                        </Typography>
                    </AlertTitle>
                    <Typography>{translate('drawers.sendVisitToRevision.info.message')}</Typography>
                </Alert>
                <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label={translate('drawers.sendVisitToRevision.fields.consultora')}
                            value={consultora}
                            fullWidth
                            size="small"
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label={translate('drawers.sendVisitToRevision.fields.preventor')}
                            value={nombrePreventor}
                            fullWidth
                            size="small"
                            disabled
                        />
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label={translate('drawers.sendVisitToRevision.fields.returnMotive')}
                        multiline
                        rows={4}
                        fullWidth
                        variant="outlined"
                        value={motive}
                        inputProps={{ maxLength: 500 }}
                        onChange={(e) => setMotive(e.target.value)}
                        helperText={`${motive.length}/500`}
                        error={motive.length > 0 && motive.length < 10}
                    />
                </Grid>
            </Grid>
            <Grid
                container
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                sx={{ marginTop: '1rem' }}
            >
                <Box
                    sx={{
                        '& > :not(style)': { m: 1 },
                        display: 'flex'
                    }}
                >
                    <ButtonActions
                        handleClose={closeDrawer}
                        renderBackAction
                        returnText={translate('drawers.sendVisitToRevision.buttons.cancel')}
                        confirmText={translate(
                            'drawers.sendVisitToRevision.buttons.sendToRevision'
                        )}
                        loading={false}
                        disabled={false}
                        onClick={handleSubmit}
                        borderRadius="4px"
                        size="small"
                    />
                </Box>
            </Grid>
            <BackdropComponent loading={isSendToRevisionLoading} />
        </Box>
    );
};

export { SendVisitToRevisionDrawer };
